import { useState, useCallback, useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import Editor, { type Monaco } from "@monaco-editor/react";
import { ChevronDown, ChevronRight, Download, MonitorUp } from "lucide-react";
import { cn } from "@/lib/studio/utils";
import { registerAppMonacoThemes } from "@/lib/studio/monacoTheme";
import {
  CodeBlockCopyButton,
  StreamingIndicator,
} from "./SkillCodeBlockParts";

interface CustomRendererProps {
  code: string;
  isIncomplete?: boolean;
  language: string;
  meta?: string;
}

const APEX_LIKE = new Set([
  "apex", "apexclass", "apextrigger", "apextest", "java", "soql", "sql",
  "visualforce", "aura", "lwc",
]);

const MONACO_LANG_ALIAS: Record<string, string> = {
  apex: "java",
  apexclass: "java",
  apextrigger: "java",
  apextest: "java",
  diff: "diff",
  patch: "diff",
  soql: "sql",
  visualforce: "xml",
  lwc: "javascript",
  aura: "javascript",
};

// No internal scroll — Monaco renders at full content height.
// The outer report container handles vertical scrolling.
const MONACO_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: 13,
  lineNumbers: "on" as const,
  folding: true,
  wordWrap: "on" as const,
  renderLineHighlight: "none" as const,
  scrollbar: {
    vertical: "hidden" as const,
    horizontal: "auto" as const,
    // Pass scroll events to the parent when editor has nothing to scroll —
    // otherwise Monaco swallows wheel events even on a full-height editor.
    alwaysConsumeMouseWheel: false,
  },
  overviewRulerLanes: 0,
};

const LINE_HEIGHT = 19;
const PADDING = 16;
// Defer rendering for very large files — show a "Open editor" button instead
const DEFER_MONACO_AFTER_LINES = 500;
const DEFER_MONACO_AFTER_CHARS = 40_000;

interface MakeRendererOpts {
  isStreaming: boolean;
}

function makeCodeRenderer(opts: MakeRendererOpts) {
  return function SkillCodeBlock({ code, language, meta }: CustomRendererProps) {
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === "dark";
    const monacoRef = useRef<Monaco | null>(null);
    const [copied, setCopied] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const codeStr = code.replace(/\n$/, "");
    const lineCount = codeStr.split("\n").length;
    const monacoLang = MONACO_LANG_ALIAS[language] ?? language;
    const filename = meta?.match(/(?:filename|name)=([^\s]+)/)?.[1];
    const shouldDeferEditor =
      opts.isStreaming ||
      lineCount > DEFER_MONACO_AFTER_LINES ||
      codeStr.length > DEFER_MONACO_AFTER_CHARS;
    const [editorOpen, setEditorOpen] = useState(false);

    const beforeMonacoMount = useCallback((monaco: Monaco) => {
      monacoRef.current = monaco;
      registerAppMonacoThemes(monaco, document.documentElement.classList.contains("dark"));
    }, []);

    useEffect(() => {
      if (monacoRef.current) {
        registerAppMonacoThemes(monacoRef.current, isDark);
      }
    }, [isDark]);

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeStr);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {
        /* clipboard blocked */
      }
    }, [codeStr]);

    const handleDownload = useCallback(() => {
      const blob = new Blob([codeStr], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `snippet.${extensionFor(language)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [codeStr, filename, language]);

    // Monaco auto-height: start with an estimate, then let Monaco report its
    // exact content height via onMount / onDidContentSizeChange.
    const [editorHeight, setEditorHeight] = useState(lineCount * LINE_HEIGHT + PADDING);
    const showEditor = !shouldDeferEditor || editorOpen;
    const isDiffFence = language === "diff" || language === "patch" || meta?.includes("diff");

    if (isDiffFence) {
      return (
        <UnifiedDiffBlock
          code={codeStr}
          language={language}
          filename={filename}
          lineCount={lineCount}
          copied={copied}
          onCopy={handleCopy}
          onDownload={handleDownload}
        />
      );
    }

    return (
      <div className="my-4 w-full min-w-0">
        <div className="rounded-lg border border-border/60 bg-card overflow-hidden min-w-0">
          {/* Header — collapse arrow on the left, actions on the right */}
          <div className="flex items-center gap-2 rounded-t-lg px-2 py-1.5 bg-muted/40">
            {/* Collapse toggle — left-most, GitHub-style */}
            <button
              type="button"
              title={isCollapsed ? "Expand" : "Collapse"}
              onClick={() => setIsCollapsed((v) => !v)}
              className="flex shrink-0 items-center rounded p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted/80 hover:text-foreground"
            >
              {isCollapsed
                ? <ChevronRight className="h-3.5 w-3.5" />
                : <ChevronDown className="h-3.5 w-3.5" />}
            </button>

            {/* File info */}
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {language}
              </span>
              {filename ? (
                <span className="max-w-[180px] truncate text-xs font-medium text-foreground/70">
                  {filename}
                </span>
              ) : null}
              <span className="whitespace-nowrap text-xs text-muted-foreground/80">
                {lineCount} lines
              </span>
              <StreamingIndicator active={opts.isStreaming} />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-1">
              {shouldDeferEditor && !editorOpen ? (
                <button
                  type="button"
                  title="Open editor"
                  onClick={() => setEditorOpen(true)}
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
                >
                  <MonitorUp className="h-3.5 w-3.5" />
                  <span>Open editor</span>
                </button>
              ) : null}
              <CodeBlockCopyButton show={true} isCopied={copied} onCopy={handleCopy} />
              <button
                type="button"
                title="Download"
                onClick={handleDownload}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Download</span>
              </button>
            </div>
          </div>

          {/* Content — hidden when collapsed */}
          {!isCollapsed && (
            showEditor ? (
              <Editor
                height={`${editorHeight}px`}
                language={monacoLang}
                value={codeStr}
                theme={isDark ? "ncoder-dark" : "ncoder-light"}
                beforeMount={beforeMonacoMount}
                options={MONACO_OPTIONS}
                onMount={(editor) => {
                  const update = () => setEditorHeight(editor.getContentHeight());
                  update();
                  editor.onDidContentSizeChange(update);
                }}
              />
            ) : (
              <div className="nc-diff-scroll w-full overflow-x-auto">
                <pre className="min-w-max p-3 text-xs leading-5 text-foreground">
                  <code className="whitespace-pre">{codeStr}</code>
                </pre>
              </div>
            )
          )}
        </div>
      </div>
    );
  };
}

function UnifiedDiffBlock({
  code,
  language,
  filename,
  lineCount,
  copied,
  onCopy,
  onDownload,
}: {
  code: string;
  language: string;
  filename?: string;
  lineCount: number;
  copied: boolean;
  onCopy: () => void;
  onDownload: () => void;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isCollapsed, setIsCollapsed] = useState(false);
  const rows = code.split("\n");

  // Diff line colours adapted for dark/light backgrounds.
  const diffStyles = isDark
    ? {
        add:     { bg: "rgba(34, 197, 94, 0.16)",  fg: "#86efac" },
        del:     { bg: "rgba(239, 68, 68, 0.18)",  fg: "#fca5a5" },
        hunk:    { bg: "rgba(59, 130, 246, 0.16)", fg: "#93c5fd" },
        file:    { bg: "rgba(148, 163, 184, 0.12)",fg: "#cbd5e1" },
        context: { bg: "transparent",               fg: "#d1d5db" },
      }
    : {
        add:     { bg: "rgba(22, 163, 74, 0.10)",  fg: "#15803d" },
        del:     { bg: "rgba(220, 38, 38, 0.10)",  fg: "#dc2626" },
        hunk:    { bg: "rgba(37, 99, 235, 0.10)",  fg: "#2563eb" },
        file:    { bg: "rgba(71, 85, 105, 0.10)",  fg: "#475569" },
        context: { bg: "transparent",               fg: "#1e293b" },
      };

  return (
    /*
     * w-full + min-w-0: fills the Streamdown flex wrapper (which renders our
     * component as a flex-col item) while allowing shrink in tighter parents.
     * No overflow:hidden here — that would create a second clipping boundary
     * above the scroll container and can cause scrollLeft to initialise
     * non-zero in Chrome/Safari when an ancestor has min-width:0.
     */
    <div className="my-4 w-full min-w-0">
      {/*
       * flex-col stacks header + scroll body. No overflow:hidden so the
       * horizontal scrollbar from the child is never clipped. The rounded
       * border and background are purely visual and don't need clip.
       */}
      <div className={cn(
        "flex flex-col rounded-lg border border-border/60 min-w-0",
        isDark ? "bg-slate-900" : "bg-slate-50"
      )}>
        {/* Header */}
        <div className={cn(
          "flex shrink-0 items-center gap-2 rounded-t-lg px-2 py-1.5",
          isDark ? "bg-slate-900/80" : "bg-slate-100"
        )}>
          {/* Collapse toggle */}
          <button
            type="button"
            title={isCollapsed ? "Expand" : "Collapse"}
            onClick={() => setIsCollapsed((v) => !v)}
            className={cn(
              "flex shrink-0 items-center rounded p-0.5 transition-colors",
              isDark
                ? "text-slate-500 hover:bg-white/10 hover:text-slate-300"
                : "text-muted-foreground/60 hover:bg-muted/80 hover:text-foreground"
            )}
          >
            {isCollapsed
              ? <ChevronRight className="h-3.5 w-3.5" />
              : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          {/* File info */}
          <div className="flex flex-1 items-center gap-2 min-w-0">
            <span className={cn(
              "text-xs font-semibold uppercase tracking-wide",
              isDark ? "text-slate-300" : "text-foreground/70"
            )}>
              {language || "diff"}
            </span>
            {filename ? (
              <span className={cn(
                "max-w-[180px] truncate text-xs font-medium",
                isDark ? "text-slate-400" : "text-muted-foreground"
              )}>
                {filename}
              </span>
            ) : null}
            <span className={cn(
              "whitespace-nowrap text-xs",
              isDark ? "text-slate-500" : "text-muted-foreground/70"
            )}>
              {lineCount} lines
            </span>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-1">
            <CodeBlockCopyButton show={true} isCopied={copied} onCopy={onCopy} />
            <button
              type="button"
              title="Download"
              onClick={onDownload}
              className={cn(
                "flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors",
                isDark
                  ? "text-slate-400 hover:bg-white/10 hover:text-white"
                  : "text-muted-foreground hover:bg-muted/80 hover:text-foreground"
              )}
            >
              <Download className="h-3.5 w-3.5" />
              <span>Download</span>
            </button>
          </div>
        </div>

        {/* Diff content — horizontally scrollable when expanded */}
        {!isCollapsed && (
          /*
           * nc-diff-scroll: custom slim scrollbar (see globals.css).
           * overflow-x-auto: the ONE and ONLY horizontal scroll boundary.
           * w-full ensures it fills the card width on narrow viewports.
           */
          <div className="nc-diff-scroll w-full overflow-x-auto">
            {/*
             * min-w-max is the architectural key: it forces <pre> to be at
             * least as wide as its longest content line, so the scroll
             * container's scrollWidth equals the true content width and
             * scrollLeft always initialises at 0 (showing the line-start).
             *
             * Do NOT use <code> here — when rendered inside .ma-prose,
             * the prose rule `.ma-prose code { padding:2px 6px; border:1px solid }`
             * adds a border to a block element inside the scroll container,
             * triggering a Chrome/Safari bug where scrollLeft starts non-zero.
             */}
            <pre className="min-w-max p-0 text-xs leading-5">
              {rows.map((line, index) => {
                const kind =
                  line.startsWith("+") && !line.startsWith("+++") ? "add" :
                  line.startsWith("-") && !line.startsWith("---") ? "del" :
                  line.startsWith("@@") ? "hunk" :
                  line.startsWith("+++") || line.startsWith("---") ? "file" :
                  "context";
                const style = diffStyles[kind];
                return (
                  /*
                   * <div> (block by default) as a direct child of <pre>.
                   * width: 100% of <pre> = min-w-max, so the row background
                   * always fills the full visible + scrollable width.
                   */
                  <div
                    key={`${index}-${kind}`}
                    style={{
                      paddingLeft: "0.75rem",
                      paddingRight: "0.75rem",
                      whiteSpace: "pre",
                      background: style.bg,
                      color: style.fg,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    }}
                  >
                    {line || " "}
                  </div>
                );
              })}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function extensionFor(language: string): string {
  if (APEX_LIKE.has(language)) return "cls";
  const map: Record<string, string> = {
    typescript: "ts",
    javascript: "js",
    python: "py",
    yaml: "yml",
    markdown: "md",
    bash: "sh",
    shell: "sh",
  };
  return map[language] ?? language ?? "txt";
}

const SUPPORTED_LANGS = [
  // Apex / SF
  "apex", "apexclass", "apextrigger", "apextest", "soql", "sql", "visualforce", "aura", "lwc",
  // General
  "java", "javascript", "typescript", "jsx", "tsx", "python", "ruby", "go",
  "rust", "kotlin", "swift", "c", "cpp", "csharp", "php", "html", "css",
  "bash", "shell", "sh", "zsh", "powershell", "diff", "patch",
];

export function buildSkillCodeRenderer(opts: MakeRendererOpts) {
  return {
    language: SUPPORTED_LANGS,
    component: makeCodeRenderer(opts),
  };
}
